import { useEffect } from 'react';
import { EventType } from './EventType';
import { Log } from './Log';

import * as Telemetry from './Telemetry';

// La idea está sacada de https://medium.com/@bterlson/strongly-typed-event-emitters-2c2345801de8

type MatchingKeys<TRecord, TMatch, K extends keyof TRecord = keyof TRecord> =
	K extends (TRecord[K] extends TMatch ? K : never) ? K : never;

type AllKeys<TRecord> = keyof TRecord;
type VoidKeys<TRecord> = MatchingKeys<TRecord, void>;
type NotVoidKeys<TRecord> = Exclude<AllKeys<TRecord>, VoidKeys<TRecord>>;

export type AllEvents = AllKeys<EventType>;
export type VoidEvents = VoidKeys<EventType>;
export type NotVoidEvents = NotVoidKeys<EventType>;

export type EventListener<P extends keyof EventType> = (m: EventType[P]) => Promise<void>;

interface EventToListeners<P extends AllEvents> {
	[key: string]: EventListener<P>[];
}
const listeners: EventToListeners<AllEvents> = {};

export function on<P extends NotVoidEvents>(event: P, listener: EventListener<P>): () => void;
export function on<P extends VoidEvents>(event: P, listener: () => Promise<void>): () => void;

export function on(event: any, listener: EventListener<any>) {
	listeners[event] = listeners[event] || [];
	listeners[event].push(listener);

	return () => {
		off(event, listener);
	};
}

export async function raise<P extends NotVoidEvents>(event: P, params: EventType[P]): Promise<void>;
export async function raise<P extends VoidEvents>(event: P): Promise<void>;

export async function raise(event: any, params?: any): Promise<void> {
	Log.debug(`event["${event}"].raise()`);

	const start = performance.now();

	if (event in listeners) {
		// Necesito hacer una copia porque podría modificarse la lista de
		// manejadores mientras se ejecutan. Por ejemplo, podría haber un
		// manejador que como parte de su proceso se elimina de la lista;
		// eso cambiaría el length y haría que no se disparasen todos.
		const eventListeners = [...listeners[event]];
		while (eventListeners.length) {
			const listener = eventListeners.pop();
			await listener!(params);
		}
	}

	const duration = performance.now() - start;

	Telemetry.event(event, { duration });
}

export function off<P extends NotVoidEvents>(event: P, listener: EventListener<P>): void;
export function off<P extends VoidEvents>(event: P, listener: () => Promise<void>): void;

export function off(event: any, listener: EventListener<any>) {
	if (event in listeners) {
		const idx = listeners[event].indexOf(listener);
		if (idx === -1) {
			throw Error(`Se ha intentado eliminar un listener del evento ${event} que no estaba registrado o ya había sido eliminado.`);
		}
		listeners[event].splice(idx, 1);
	}
}

export function useEventOn<P extends NotVoidEvents>(event: P, listener: EventListener<P>): void;
export function useEventOn<P extends VoidEvents>(event: P, listener: () => Promise<void>): void;
export function useEventOn(event: any, listener: EventListener<any>) {

	return useEffect(() => {
		return on(event, listener);
	});
}